import { useContext, useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation } from 'js/lib/useRouter';

import { NativeDataContext } from 'bundles/ent-website/components/NativeDataContext';
import FindByPromoCodeId from 'bundles/ent-website/utils/FindByPromoCodeId.graphql';
import type {
  FindByEnterprisePromotionIdQuery as FindByEnterprisePromotionIdQueryType,
  FindByEnterprisePromotionIdQueryVariables,
} from 'bundles/ent-website/utils/__generated__/FindByEnterprisePromotionIdQuery';
import type {
  FindByPromoCodeIdQuery,
  FindByPromoCodeIdQueryVariables,
} from 'bundles/ent-website/utils/__generated__/FindByPromoCodeId';
import { findByEnterprisePromotionIdQuery } from 'bundles/ent-website/utils/withEnterprisePromotionQuery';

function PromoDataSource() {
  const { query } = useLocation();
  const promoCode = query?.promoCode;

  // This query will only run when promo code is present in the URL
  const { data: findByPromoCodeIdData } = useQuery<FindByPromoCodeIdQuery, FindByPromoCodeIdQueryVariables>(
    FindByPromoCodeId,
    {
      skip: !promoCode,
      variables: { promoCodeId: simplifyQueryValue(promoCode)! },
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
    }
  );

  const enterprisePromotion = useMemo(() => {
    if (!findByPromoCodeIdData) return {};

    return {
      id: findByPromoCodeIdData.EnterprisePromotionQueries?.getByPromoCodeId.enterprisePromotion?.id,
      promotionId: findByPromoCodeIdData.EnterprisePromotionQueries?.getByPromoCodeId.enterprisePromotion?.promotion.id,
    };
  }, [findByPromoCodeIdData]);

  const { data: findByPromoIdData } = useQuery<
    FindByEnterprisePromotionIdQueryType,
    FindByEnterprisePromotionIdQueryVariables
  >(findByEnterprisePromotionIdQuery, {
    skip: !enterprisePromotion?.id,
    variables: { id: enterprisePromotion.id ?? '' },
    errorPolicy: 'all',
  });

  const promotion = findByPromoIdData?.PromotionsV3Resource?.byEnterprisePromotionId?.elements?.[0];

  const { setNativeData } = useContext(NativeDataContext);

  useEffect(() => {
    setNativeData('promo', {
      promoCode,
      percent: promotion?.discountPercent,
      name: promotion?.promotionName,
      promotion,
      enterprisePromotion,
    });
  }, [setNativeData, promoCode, promotion, enterprisePromotion]);

  return null;
}

export default PromoDataSource;
